<script setup>
import { router, useForm, usePage, usePoll } from '@inertiajs/vue3'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import PromotionApprovalDialog from '@/components/admin/PromotionApprovalDialog.vue'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  runs: { type: Array, default: () => [] },
  activeDefault: { type: Object, default: () => ({}) },
  promotions: { type: Array, default: () => [] },
  datasetStats: { type: Object, default: () => ({}) },
  formDefaults: { type: Object, default: () => ({}) },
  explorerUrl: { type: String, default: '' },
})

const page = usePage()
const flash = computed(() => page.props.flash ?? {})

// Launch form — the inputs the NSGA-II pipeline actually consumes (holdout size + freshness
// window, test-case inclusion, feature-vector cache). The old optimize-weights knobs
// (pool_limit / n_similar / α / β / cap) were removed: the NSGA job ignores them.
const form = useForm({
  limit: props.formDefaults.limit ?? 100,
  months: props.formDefaults.months ?? 1,
  include_test_cases: props.formDefaults.include_test_cases ?? true,
  use_cache: props.formDefaults.use_cache ?? true,
  name: '',
})

const launch = () => {
  // The run row is created `pending` server-side and returned in the redirect-back `runs`, so it
  // shows instantly; the poll watcher below then streams its progress.
  form.post(route('admin.similarity-calibration.run'), { preserveScroll: true })
}

// Live progress: poll the runs list while any run is still working, stop once all are terminal.
// Mirrors the Upload.vue pattern (Inertia v3 usePoll, autoStart:false, partial reload).
const POLL_INTERVAL = 2500
const anyActive = computed(() => props.runs.some(r => r.status === 'pending' || r.status === 'running'))
const { start, stop } = usePoll(POLL_INTERVAL, { only: ['runs'] }, { autoStart: false })
watch(anyActive, active => (active ? start() : stop()), { immediate: true })
onBeforeUnmount(stop)

// RED-355 B3: "Rakenda" is an approved promotion — the dialog collects the approver, the
// Jira link and the reason; a run never changes the served model on its own.
const promotingRun = ref(null)
const approvalVisible = ref(false)
const promote = run => {
  promotingRun.value = run
  approvalVisible.value = true
}
const promotionUrl = computed(() =>
  promotingRun.value ? route('admin.similarity-calibration.promote', promotingRun.value.id) : '',
)
const onPromoted = () => router.reload({ only: ['runs', 'activeDefault', 'promotions'] })
const scopeLabel = scope =>
  ({
    default_ebm: 'vaikemudel',
    similarity_config: 'sarnasuskaalud',
    segmentation: 'segmendid',
    servability: 'serveeritavus',
    config_pin: 'konfiguratsiooni pin',
  })[scope] ?? scope

const expanded = ref({})

const pct = v => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`)
const loss = v => (v === null || v === undefined ? '—' : Number(v).toFixed(3))
const weights = w => (Array.isArray(w) ? w.map(x => Number(x).toFixed(2)).join(' · ') : '—')
const date = iso => (iso ? new Date(iso).toLocaleString('et-EE') : '—')

const statusSeverity = s =>
  ({ completed: 'success', failed: 'danger', running: 'info', pending: 'warn' })[s] ?? 'secondary'

const isActive = r => r.status === 'pending' || r.status === 'running'
const assemblyPct = r => (r.progress_total > 0 ? Math.round((r.progress_current / r.progress_total) * 100) : 0)
const showAssemblyBar = r => r.progress_stage === 'assembling' && r.progress_total > 0
const stageLabel = r =>
  ({ queued: 'Ootel järjekorras', assembling: 'Koostan andmestikku', optimizing: 'Optimeerin kaalusid' })[
    r.progress_stage
  ] ?? r.status
const elapsedSeconds = r => {
  if (!r.started_at) {
    return null
  }
  const end = r.progress_updated_at ? new Date(r.progress_updated_at) : new Date()
  return Math.max(0, Math.round((end - new Date(r.started_at)) / 1000))
}

// A run improves valuation when its best loss is below the baseline.
const improvedValuation = run =>
  run.valuation_loss !== null &&
  run.baseline_valuation_loss !== null &&
  run.valuation_loss < run.baseline_valuation_loss
</script>

<template>
  <div class="py-4">
    <div class="mb-3">
      <h1 class="text-xl font-medium">Kaalude kalibreerimine</h1>
      <p class="text-surface-500 text-sm">
        Sobitab sarnasusotsingu vaikekaalud ekspertide hinnangute järgi — milliseid
        võrdlustehinguid hindajad päriselt valisid ja millise lõppväärtuseni jõudsid.
      </p>
    </div>

    <Message v-if="flash.success" severity="success" :closable="false" class="mb-3">
      {{ flash.success }}
    </Message>
    <Message v-if="flash.error" severity="error" :closable="false" class="mb-3">
      {{ flash.error }}
    </Message>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
      <!-- Current default -->
      <Card>
        <template #title>
          <div class="flex items-center gap-2 text-xl font-medium">
            Praegune vaikeväärtus
            <Tag
              :value="activeDefault.is_calibrated ? 'Kalibreeritud' : 'Käsitsi seatud'"
              :severity="activeDefault.is_calibrated ? 'success' : 'secondary'"
            />
            <Tag
              v-if="activeDefault.scope === 'model'"
              v-tooltip.bottom="`Vaikemudelile ${activeDefault.model_type} on oma kalibreeritud konfiguratsioon; globaalne varukonfiguratsioon kehtib teistele mudelitele.`"
              value="mudelipõhine"
              severity="info"
            />
            <Tag
              v-else-if="activeDefault.scope === 'global'"
              v-tooltip.bottom="'Vaikemudelil pole oma konfiguratsiooni — kasutusel on globaalne varukonfiguratsioon.'"
              value="globaalne varukonfiguratsioon"
              severity="warn"
            />
          </div>
        </template>
        <template #content>
          <div class="grid grid-cols-2 gap-y-1 text-sm">
            <span class="text-surface-500">Serveeritav vaikemudel</span>
            <span class="font-medium break-all" data-testid="pinned-default-model">
              {{ activeDefault.model_type ?? 'määramata (kiirhindamine ei tööta)' }}
            </span>
            <span class="text-surface-500">zdist_weight (asukoht)</span>
            <span class="font-medium">{{ loss(activeDefault.zdist_weight) }}</span>
            <span class="text-surface-500">embed_weights</span>
            <span class="font-medium">{{ weights(activeDefault.embed_weights) }}</span>
            <span class="text-surface-500">price_diff_weight</span>
            <span class="font-medium">{{ loss(activeDefault.price_diff_weight) }}</span>
            <span class="text-surface-500">rikastatus_weight</span>
            <span class="font-medium">{{ loss(activeDefault.rikastatus_weight) }}</span>
          </div>
          <p v-if="activeDefault.promoted_at" class="text-surface-400 text-xs mt-2">
            Rakendatud {{ date(activeDefault.promoted_at) }} (käivitus #{{ activeDefault.source_run_id
            }})<template v-if="activeDefault.scope === 'model'"> · mudel {{ activeDefault.model_type }}</template>
          </p>
          <div
            v-if="activeDefault.global_fallback"
            class="border-t border-surface-200 dark:border-surface-700 mt-3 pt-2 text-xs text-surface-500"
          >
            <span class="font-medium">Globaalne varukonfiguratsioon</span>
            (mudelitele, millel puudub oma konfiguratsioon): zdist
            {{ loss(activeDefault.global_fallback.zdist_weight) }} · embed
            {{ weights(activeDefault.global_fallback.embed_weights) }} · price_diff
            {{ loss(activeDefault.global_fallback.price_diff_weight) }} · rikastatus
            {{ loss(activeDefault.global_fallback.rikastatus_weight) }}
            <template v-if="activeDefault.global_fallback.promoted_at">
              · rakendatud {{ date(activeDefault.global_fallback.promoted_at) }}
            </template>
          </div>
        </template>
      </Card>

      <!-- Launch -->
      <Card>
        <template #title>
          <span class="text-xl font-medium">Käivita kalibreerimine</span>
        </template>
        <template #content>
          <p class="text-surface-500 text-xs mb-3">
            Kaheastmeline käivitus, mis ainult soovitab: (1) hinnatakse toormudeleid täpsuse
            järgi ja soovitatakse parimat, (2) kalibreeritakse sellele sarnasuskaalud. Serveeritav
            mudel ja kaalud ei muutu: soovituse rakendamiseks vajuta „Rakenda“ ja nimeta
            kinnitaja. Andmestik: {{ datasetStats.test_cases }} testjuhtumit + värsked
            Harjumaa/KOM tehingud (tehingusumma tõeväärtusena, ilma ekspertvõrdlusteta).
            Käivitus võtab paar minutit.
          </p>
          <div class="grid grid-cols-2 gap-3 mb-3 max-w-md">
            <div class="flex flex-col gap-1">
              <label class="text-xs text-surface-500">Holdout tehinguid</label>
              <InputNumber v-model="form.limit" :min="1" :max="500" fluid />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs text-surface-500">Ajaperiood (kuud)</label>
              <InputNumber v-model="form.months" :min="1" :max="240" fluid />
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-4 mb-3">
            <div class="flex items-center gap-2">
              <Checkbox v-model="form.include_test_cases" :binary="true" inputId="tc" />
              <label for="tc" class="text-sm">Kaasa testjuhtumid</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox v-model="form.use_cache" :binary="true" inputId="cache" />
              <label for="cache" class="text-sm" title="Taaskasuta varem koostatud tunnusvektoreid (kiirem kordusjooks)">
                Kasuta vahemälu
              </label>
            </div>
            <InputText v-model="form.name" placeholder="Nimi (valikuline)" class="grow" />
          </div>
          <div class="flex justify-end">
            <Button
              label="Käivita kalibreerimine"
              icon="pi pi-play"
              :loading="form.processing"
              @click="launch"
            />
          </div>
        </template>
      </Card>
    </div>

    <!-- Runs -->
    <Card>
      <template #title>
        <span class="text-xl font-medium">Kalibreerimise käivitused</span>
      </template>
      <template #content>
        <DataTable
          :value="runs"
          v-model:expandedRows="expanded"
          dataKey="id"
          size="small"
          stripedRows
          :rowHover="true"
          data-testid="calibration-runs"
        >
          <Column expander style="width: 3rem" />
          <Column header="Aeg">
            <template #body="{ data }">
              <div class="text-sm">{{ date(data.created_at) }}</div>
              <div v-if="data.name" class="text-xs text-surface-400">{{ data.name }}</div>
            </template>
          </Column>
          <Column header="Olek" style="min-width: 16rem">
            <template #body="{ data }">
              <div v-if="isActive(data)" class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  <i class="pi pi-spin pi-spinner text-primary text-sm" />
                  <span class="text-sm font-medium">{{ stageLabel(data) }}</span>
                  <span v-if="showAssemblyBar(data)" class="text-surface-400 text-xs">
                    {{ data.progress_current }}/{{ data.progress_total }}
                  </span>
                  <span v-if="elapsedSeconds(data) !== null" class="text-surface-400 text-xs">
                    · {{ elapsedSeconds(data) }} s
                  </span>
                </div>
                <ProgressBar
                  v-if="showAssemblyBar(data)"
                  :value="assemblyPct(data)"
                  style="height: 0.4rem"
                />
                <ProgressBar v-else mode="indeterminate" style="height: 0.4rem" />
              </div>
              <template v-else>
                <Tag :value="data.status" :severity="statusSeverity(data.status)" />
                <Tag v-if="data.is_promoted" value="rakendatud" severity="success" class="ml-1" />
                <Tag
                  v-else-if="data.is_recommended"
                  v-tooltip.top="'Stage 2 värav läbitud: kaalud on soovitus. Rakendamine vajab kinnitajat.'"
                  value="soovitus"
                  severity="info"
                  class="ml-1"
                />
              </template>
            </template>
          </Column>
          <Column header="Hinnamudel">
            <template #body="{ data }">{{ data.chosen_price_model ?? '—' }}</template>
          </Column>
          <Column>
            <template #header>
              <span class="inline-flex items-center gap-1">
                Holdout MAPE (enne → pärast)
                <i
                  class="pi pi-info-circle text-surface-400 text-xs cursor-help"
                  v-tooltip.top="'Holdout = värsked, mudelile tundmatud tehingud (üldistuse test). MAPE = keskmine protsentuaalne viga tegelikust tehinguhinnast; väiksem on parem. „Enne → pärast“ võrdleb praeguse vaikeseade viga kalibreeritud kaalude vigaga. See on PEAMINE mõõdik — kas kalibreerimine üldistub uutele tehingutele. Roheline = paranes.'"
                />
              </span>
            </template>
            <template #body="{ data }">
              <span class="text-surface-400">{{ pct(data.baseline_holdout_mape) }}</span>
              <i class="pi pi-arrow-right mx-1 text-xs" />
              <span class="font-medium" :class="{ 'text-green-600': improvedValuation(data) }">
                {{ pct(data.holdout_mape) }}
              </span>
            </template>
          </Column>
          <Column>
            <template #header>
              <span class="inline-flex items-center gap-1">
                Testjuhtumid MAPE (enne → pärast)
                <i
                  class="pi pi-info-circle text-surface-400 text-xs cursor-help"
                  v-tooltip.top="'Testjuhtumid = eksperdi käsitsi hinnatud kontrolljuhtumid (puhtad, linnasisesed ankrud). Näitab, kas kalibreerimine ei halvenda tuntud juhtumite täpsust — kaitse ülesobitamise vastu. Väiksem on parem.'"
                />
              </span>
            </template>
            <template #body="{ data }">
              <span class="text-surface-400">{{ pct(data.baseline_test_mape) }}</span>
              <i class="pi pi-arrow-right mx-1 text-xs" />
              <span class="font-medium">{{ pct(data.test_mape) }}</span>
            </template>
          </Column>
          <Column>
            <template #header>
              <span class="inline-flex items-center gap-1">
                Ebastabiilsus (CV std)
                <i
                  class="pi pi-info-circle text-surface-400 text-xs cursor-help"
                  v-tooltip.top="'Ristvalideerimise standardhälve: kui palju täpsus kõigub, kui andmed jagada osadeks. Väiksem = stabiilsem, usaldusväärsem tulemus (kaalud ei sobitu juhusliku müraga). Nt 0,4% on väga stabiilne.'"
                />
              </span>
            </template>
            <template #body="{ data }">{{ pct(data.instability) }}</template>
          </Column>
          <Column header="Tegevused">
            <template #body="{ data }">
              <Button
                v-if="data.status === 'completed' && !data.is_promoted"
                label="Rakenda"
                size="small"
                icon="pi pi-check"
                :data-testid="`promote-run-${data.id}`"
                @click="promote(data)"
              />
              <span v-else-if="data.is_promoted" class="text-green-600 text-sm">
                <i class="pi pi-check" /> aktiivne
              </span>
              <span v-else class="text-surface-400 text-sm">—</span>
            </template>
          </Column>

          <template #expansion="{ data }">
            <div class="p-3 bg-surface-50">
              <div v-if="data.error" class="text-red-600 text-sm mb-2">Viga: {{ data.error }}</div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div class="font-medium text-sm mb-1">Soovitatud kaalud</div>
                  <div class="grid grid-cols-2 gap-y-1 text-sm">
                    <span class="text-surface-500">zdist_weight</span>
                    <span class="font-medium">{{ loss(data.best_config?.zdist_weight) }}</span>
                    <span class="text-surface-500">embed_weights</span>
                    <span class="font-medium">{{ weights(data.best_config?.embed_weights) }}</span>
                    <span class="text-surface-500">price_diff_weight</span>
                    <span class="font-medium">{{ loss(data.best_config?.price_diff_weight) }}</span>
                    <span class="text-surface-500">rikastatus_weight</span>
                    <span class="font-medium">{{ loss(data.best_config?.rikastatus_weight) }}</span>
                    <span class="text-surface-500">cap_m</span>
                    <span class="font-medium">{{ data.best_config?.cap_m ?? '—' }}</span>
                  </div>
                </div>
                <div class="text-sm text-surface-500">
                  <div>Soovitatud mudel (Stage 1): {{ data.stage1_best ?? '—' }}</div>
                  <div>Subjekte: {{ data.n_subjects ?? '—' }}</div>
                  <div>Pareto-konfiguratsioone: {{ data.pareto_count }}</div>
                  <div>Kestus: {{ data.duration_ms ? Math.round(data.duration_ms / 1000) + ' s' : '—' }}</div>
                  <div>
                    Parameetrid: holdout {{ data.params?.holdout_limit }}, {{ data.params?.months }} kuud
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template #empty>
            <div class="text-center text-surface-400 py-6">Kalibreerimisi pole veel tehtud.</div>
          </template>
        </DataTable>
        <div class="mt-3 text-right">
          <a :href="explorerUrl" class="text-primary text-sm hover:underline">
            <i class="pi pi-map" /> Ava Mudeli ülevaade kaalude kontrollimiseks
          </a>
        </div>
      </template>
    </Card>

    <!-- Promotion log (RED-355 B3) -->
    <Card class="mt-4">
      <template #title>
        <span class="text-xl font-medium">Edutamiste logi</span>
      </template>
      <template #content>
        <DataTable :value="promotions" dataKey="id" size="small" stripedRows data-testid="promotion-log">
          <Column header="Aeg">
            <template #body="{ data }">{{ date(data.executed_at) }}</template>
          </Column>
          <Column header="Mis muutus">
            <template #body="{ data }">
              <div class="text-sm">{{ scopeLabel(data.scope) }}: <span class="break-all">{{ data.target ?? '—' }}</span></div>
              <div v-if="data.previous && data.previous !== data.model" class="text-xs text-surface-400 break-all">
                enne: {{ data.previous }}
              </div>
            </template>
          </Column>
          <Column header="Kinnitaja">
            <template #body="{ data }">
              <a v-if="data.approval_link" :href="data.approval_link" target="_blank" rel="noopener" class="text-primary hover:underline">
                {{ data.approved_by ?? '—' }}
              </a>
              <span v-else>{{ data.approved_by ?? '—' }}</span>
              <div class="text-xs text-surface-400">tegi: {{ data.executed_by }}</div>
            </template>
          </Column>
          <Column header="Reegel">
            <template #body="{ data }">
              <Tag v-if="data.rule_passed" value="läbitud" severity="success" />
              <template v-else>
                <Tag value="erand" severity="warn" />
                <div v-if="data.override_reason" class="text-xs text-surface-500 mt-1">{{ data.override_reason }}</div>
              </template>
            </template>
          </Column>
          <template #empty>
            <div class="text-center text-surface-400 py-4">Edutamisi pole veel.</div>
          </template>
        </DataTable>
      </template>
    </Card>

    <PromotionApprovalDialog
      v-model:visible="approvalVisible"
      :url="promotionUrl"
      title="Rakenda kalibreeritud kaalud"
      :subject="promotingRun ? `Käivitus #${promotingRun.id}${promotingRun.name ? ' · ' + promotingRun.name : ''}` : ''"
      @promoted="onPromoted"
    />
  </div>
</template>
